import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, Award, Users, ShieldCheck, CheckCircle } from "lucide-react";
import * as Icons from "lucide-react";

const DOCTOR_IMG_1 = "https://customer-assets.emergentagent.com/job_dr-burcu-portal/artifacts/zfxcb01u_dsasdwwq.jpg";
const DOCTOR_IMG_2 = "https://customer-assets.emergentagent.com/job_dr-burcu-portal/artifacts/feh6ks01_sadwq.jpg";

export default function HomePage() {
  const { t } = useLanguage();
  const categories = t("services.categories");
  const reasons = t("why_visit.reasons");

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#FDFBF7]">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 space-y-8 animate-fadeInUp">
              <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#4A6753] bg-[#4A6753]/10 px-4 py-2 rounded-full">
                {t("hero.overline")}
              </span>
              <h1 data-testid="hero-title" className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none text-[#1E2922] font-semibold">
                {t("hero.title")}
              </h1>
              <p className="text-lg leading-relaxed text-[#566D60] max-w-xl">
                {t("hero.subtitle")}
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/contact" data-testid="hero-contact-button" className="inline-flex items-center gap-2 bg-[#4A6753] text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#3A5242] transition-colors shadow-sm group">
                  {t("hero.cta")}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/about" data-testid="hero-about-button" className="inline-flex items-center gap-2 bg-transparent border border-[#4A6753] text-[#4A6753] rounded-full px-8 py-3.5 font-medium hover:bg-[#F4F1EA] transition-colors">
                  {t("hero.secondary_cta")}
                </Link>
              </div>
            </div>
            <div className="md:col-span-5 animate-fadeInUp animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#C9A988]/20 rounded-3xl blur-2xl" />
                <img src={DOCTOR_IMG_1} alt="Dr. Burcu Varol" data-testid="hero-doctor-image" className="relative w-full rounded-2xl shadow-xl object-cover aspect-[3/4]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#4A6753] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div data-testid="stat-experience" className="space-y-1">
              <Award className="mx-auto text-[#C9A988] mb-2" size={28} />
              <div className="text-3xl font-heading font-semibold text-white">{t("about.stats.experience")}</div>
              <div className="text-sm text-white/70">{t("about.stats.experience_label")}</div>
            </div>
            <div data-testid="stat-patients" className="space-y-1">
              <Users className="mx-auto text-[#C9A988] mb-2" size={28} />
              <div className="text-3xl font-heading font-semibold text-white">{t("about.stats.patients")}</div>
              <div className="text-sm text-white/70">{t("about.stats.patients_label")}</div>
            </div>
            <div data-testid="stat-certifications" className="space-y-1">
              <ShieldCheck className="mx-auto text-[#C9A988] mb-2" size={28} />
              <div className="text-3xl font-heading font-semibold text-white">{t("about.stats.certifications")}</div>
              <div className="text-sm text-white/70">{t("about.stats.certifications_label")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why visit section */}
      <section data-testid="why-visit-section" className="py-24 bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
            <div className="md:col-span-5">
              <img src={DOCTOR_IMG_2} alt="Dr. Burcu Varol" className="w-full rounded-2xl shadow-lg object-cover aspect-[3/4]" />
            </div>
            <div className="md:col-span-7 space-y-6">
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1E2922]">
                {t("why_visit.title")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Array.isArray(reasons) && reasons.map((reason, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#4A6753] mt-1 shrink-0" />
                    <span className="text-sm text-[#566D60] leading-relaxed">{reason}</span>
                  </div>
                ))}
              </div>
              <p className="text-base font-medium text-[#4A6753] italic pt-2">
                {t("why_visit.footer")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section data-testid="services-preview" className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4A6753]">
              {t("services.overline")}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1E2922]">
              {t("services.title")}
            </h2>
            <p className="text-base leading-relaxed text-[#566D60] max-w-2xl mx-auto">
              {t("services.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(categories) && categories.map((cat, i) => {
              const IconComponent = Icons[cat.icon] || Icons.Activity;
              return (
                <Link key={i} to={cat.link} data-testid={`service-card-${i}`} className="bg-[#F4F1EA] rounded-2xl p-8 border border-transparent hover:border-[#E2E0D9] transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md block">
                  <div className="w-14 h-14 rounded-xl bg-[#4A6753]/10 flex items-center justify-center mb-6">
                    <IconComponent size={28} className="text-[#4A6753]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-medium text-[#1E2922] mb-3 font-heading">{cat.title}</h3>
                  <p className="text-sm leading-relaxed text-[#566D60]">{cat.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#4A6753]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center space-y-8">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white">
            {t("contact.title")}
          </h2>
          <p className="text-base leading-relaxed text-white/70">
            {t("contact.subtitle")}
          </p>
          <Link to="/contact" data-testid="cta-contact-button" className="inline-flex items-center gap-2 bg-white text-[#4A6753] rounded-full px-8 py-3.5 font-medium hover:bg-[#F4F1EA] transition-colors shadow-sm">
            {t("hero.cta")}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
